<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="mb-0">
                <strong>Program-Specific Account Manager Overrides</strong>
            </label>
            <b-button v-if="!disableFields" size="sm" variant="primary" @click="showAddModal">
                <i class="fa fa-plus"></i> Add Override
            </b-button>
        </div>

        <p class="text-muted small mb-3">
            Override the default account manager for specific programs. When set, the program-specific account manager
            will be used instead of the default for all prescriptions in that program.
        </p>

        <b-table
            v-if="overrides.length > 0"
            :items="overrides"
            :fields="tableFields"
            striped
            hover
            small
        >
            <template #cell(program)="data">
                <strong>{{ data.item.programName }}</strong>
                <span v-if="data.item.programAbbreviation" class="text-muted">
                    ({{ data.item.programAbbreviation }})
                </span>
            </template>

            <template #cell(accountManager)="data">
                {{ getAccountManagerDisplayName(data.item) }}
            </template>

            <template #cell(actions)="data">
                <div class="d-flex gap-2">
                    <b-button size="sm" variant="outline-primary" @click="editOverride(data.item)" :disabled="disableFields">
                        <i class="fa fa-edit"></i> Edit
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="confirmDelete(data.item)" :disabled="disableFields">
                        <i class="fa fa-trash"></i> Delete
                    </b-button>
                </div>
            </template>
        </b-table>

        <div v-else class="alert alert-info text-center">
            <i class="fa fa-info-circle"></i> No program-specific overrides configured. The default account manager will be used for all programs.
        </div>

        <!-- Add/Edit Modal -->
        <b-modal
            :id="modalId"
            :title="editingOverride?.id ? 'Edit Program Override' : 'Add Program Override'"
            size="lg"
            @ok="saveOverride"
            @hidden="resetModal"
        >
            <b-form @submit.prevent="saveOverride">
                <b-form-group label="Program" label-for="program-search">
                    <SearchComponent
                        id="program-search"
                        v-model="selectedProgram"
                        searchURL="/api/program"
                        :disabled="!!editingOverride.id"
                        placeholder="Search for a program..."
                        :required="true"
                    />
                    <small class="form-text text-muted" v-if="editingOverride.id">
                        Program cannot be changed once created. Delete and recreate to change program.
                    </small>
                </b-form-group>

                <b-form-group label="Account Manager" label-for="user-search">
                    <SearchComponent
                        id="user-search"
                        v-model="selectedUser"
                        searchURL="/api/user"
                        placeholder="Search for a user..."
                        :required="true"
                    />
                </b-form-group>
            </b-form>
        </b-modal>
    </div>
</template>

<style scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PrescriberProgramAccountManager } from '@/models/PrescriberProgramAccountManager';
import SearchComponent from '@/components/SearchComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Program } from '@/models/Program';
import { User } from '@/models/User';

@Component({
    name: "ProgramAccountManagersComponent",
    components: { SearchComponent }
})
export default class ProgramAccountManagersComponent extends Vue {
    @Prop({ required: true }) prescriberId!: number;
    @Prop({ default: false }) disableFields!: boolean;

    private overrides: PrescriberProgramAccountManager[] = [];
    private editingOverride: Partial<PrescriberProgramAccountManager> = {};
    private modalId = 'program-account-manager-modal';
    private selectedProgram: Program = new Program();
    private selectedUser: User = new User();

    private tableFields = [
        { key: 'program', label: 'Program', sortable: true },
        { key: 'accountManager', label: 'Account Manager', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-right' }
    ];

    created() {
        this.fetchOverrides();
    }

    @Watch('prescriberId')
    onPrescriberIdChanged() {
        this.fetchOverrides();
    }

    @Watch('selectedProgram')
    onProgramChanged(program: Program) {
        if (program && program.id) {
            this.editingOverride.programId = program.id;
        }
    }

    @Watch('selectedUser')
    onUserChanged(user: User) {
        if (user && user.userName) {
            this.editingOverride.accountManagerUserId = user.userName;
        }
    }

    async fetchOverrides() {
        if (!this.prescriberId) return;

        try {
            const response = await this.$http.get<PrescriberProgramAccountManager[]>(
                `/prescriber/${this.prescriberId}/ProgramAccountManagers`
            );
            this.overrides = response.data || [];
        } catch (err: any) {
            console.error('Error fetching program account manager overrides:', err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    showAddModal() {
        this.editingOverride = {
            prescriberId: this.prescriberId,
            programId: undefined,
            accountManagerUserId: ''
        };
        this.selectedProgram = new Program();
        this.selectedUser = new User();
        this.$bvModal.show(this.modalId);
    }

    editOverride(override: PrescriberProgramAccountManager) {
        this.editingOverride = { ...override };

        // Initialize selected objects with existing data
        if (override.programId) {
            this.selectedProgram = new Program({
                id: override.programId,
                name: override.programName || '',
                abbreviation: override.programAbbreviation || ''
            } as any);
        }

        if (override.accountManagerUserId) {
            this.selectedUser = new User({
                userName: override.accountManagerUserId,
                firstName: override.accountManagerFirstName || '',
                lastName: override.accountManagerLastName || ''
            } as any);
        }

        this.$bvModal.show(this.modalId);
    }

    async saveOverride(event?: Event) {
        if (event) event.preventDefault();

        if (!this.editingOverride.programId || !this.editingOverride.accountManagerUserId) {
            this.$notification(NotificationOptions.error('Please select both a program and an account manager.'));
            return;
        }

        try {
            if (this.editingOverride.id) {
                // Update existing override
                await this.$http.put(
                    `/prescriber/${this.prescriberId}/ProgramAccountManager/${this.editingOverride.id}`,
                    this.editingOverride
                );
                this.$notification(NotificationOptions.success('Program account manager override updated successfully.'));
            } else {
                // Create new override
                await this.$http.post(
                    `/prescriber/${this.prescriberId}/ProgramAccountManager`,
                    this.editingOverride
                );
                this.$notification(NotificationOptions.success('Program account manager override created successfully.'));
            }

            this.$bvModal.hide(this.modalId);
            await this.fetchOverrides();
        } catch (err: any) {
            console.error('Error saving program account manager override:', err);
            const errorMessage = err.response?.data || err.message || 'Failed to save override';
            this.$notification(NotificationOptions.error(errorMessage));
        }
    }

    confirmDelete(override: PrescriberProgramAccountManager) {
        this.$bvModal.msgBoxConfirm(
            `Are you sure you want to delete the account manager override for ${override.programName}? ` +
            `The default account manager will be used for this program.`,
            {
                title: 'Confirm Delete',
                size: 'md',
                okVariant: 'danger',
                okTitle: 'Delete',
                cancelTitle: 'Cancel',
                centered: true
            }
        ).then(async (confirmed: boolean) => {
            if (confirmed) {
                await this.deleteOverride(override);
            }
        });
    }

    async deleteOverride(override: PrescriberProgramAccountManager) {
        if (!override.id) return;

        try {
            await this.$http.delete(
                `/prescriber/${this.prescriberId}/ProgramAccountManager/${override.id}`
            );
            this.$notification(NotificationOptions.success('Program account manager override deleted successfully.'));
            await this.fetchOverrides();
        } catch (err: any) {
            console.error('Error deleting program account manager override:', err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    resetModal() {
        this.editingOverride = {};
        this.selectedProgram = new Program();
        this.selectedUser = new User();
    }

    getAccountManagerDisplayName(override: PrescriberProgramAccountManager): string {
        if (override.accountManagerFirstName || override.accountManagerLastName) {
            return `${override.accountManagerFirstName || ''} ${override.accountManagerLastName || ''}`.trim();
        }
        return override.accountManagerUserId;
    }
}
</script>
